import * as React from "react";

import { AnsweredQuestionWithId, LinkObject } from "@teamprevent/common/model";

import { ContextAwareProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireContainer";

import { OrgLevel, QuestionAnswer, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";

import * as questionIds from "@teamprevent/common/questionnaires/questionnaireConfig/genericCase/questionIds";

import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";

import {
    updateQuestion as updateQuestionAction,
    removeQuestion as removeQuestionAction
} from "@teamprevent/common/questionnaires/actions";

import { QuestionnaireConfig, getQuestionConfig, isQuestionMandatory } from "@teamprevent/common/questionnaires/questionnaireConfig/models";

import { loadOrgStructure } from "@teamprevent/common/questionnaires/components/referralForm/actions";

import { HrRepresentative } from "@teamprevent/common/hrRepresentatives/models";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { OtherServicesSection } from "./genericCaseFormComponents/otherServicesSection";
import { DataPrivacySection } from "./genericCaseFormComponents/dataPrivacySection";
import { ManagerHrSection } from "./genericCaseFormComponents/managerHrSection";
import { EmployeeDetailsSection } from "./genericCaseFormComponents/employeeDetailsSection";
import { EmployeeDetailsContinuedSection } from "./genericCaseFormComponents/employeeDetailsContinuedSection";
import { SupportingDocumentSection } from "./genericCaseFormComponents/supportingDocumentsSection";
import { ConfidentialitySection } from "./genericCaseFormComponents/confidentialitySection";
import { FurtherInformationSection } from "./genericCaseFormComponents/furtherInformation";
import { FurtherQuestionsSection } from "./genericCaseFormComponents/furtherQuestions";

import { push } from "redux-little-router";

export const GENERIC_CASE_QUESTIONNAIRE_KEY = "genericCase";
export const lineManagerHrSectionId = "LineManagerHR";
export const employeeDetailSectionId = "ReferredDetails";
export const furtherDetailsSectionId = "FurtherDetails";
export const documentsSectionId = "Documents";
export const confidentialitySectionId = "confidentiality";
export const furtherInformationSectionId = "furtherInformation";

export const totallyConfidentialAnswer = "Totally confidential";

const orgLevelQuestionId = "OrgLevel";

interface Props {
    readonly: boolean;
    genericCaseTypes: LinkObject[];
    currentUser?: { name: string; id: string; email: string; mobilePhone?: string; roles: string[] };
    orgLevels: OrgLevel[];
    hrRepresentatives: HrRepresentative[];
    questions: AnsweredQuestionWithId[];
    questionnaireConfig: QuestionnaireConfig;
    submitQuestionnaire?: (answers: AnsweredQuestionWithId[]) => Promise<boolean>;
}

export const GenericCaseForm: React.FunctionComponent<Props> = (
    {
        readonly,
        genericCaseTypes,
        currentUser,
        orgLevels,
        hrRepresentatives,
        questions,
        questionnaireConfig,
        submitQuestionnaire: submitQuestionnaireAction,
    }) =>
{
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

    const [selectedOrgStructureLevelIds, setSelectedOrgStructureLevelIds] = React.useState<string[]>([]);

    const moreSectionsForGenericForm = getFeatureToggles()[FeatureToggleNames.MoreSectionsForGenericForm];

    const loadOrganisationStructure = React.useCallback((parentOrgStructureLevelId: string) => {
        dispatch(loadOrgStructure(parentOrgStructureLevelId));
    }, [dispatch]);

    const getAnswerToQuestionWithId = React.useCallback((id: string): QuestionAnswer => {
        return questions.find(question => question.id === id)?.answer;
    }, [questions]);

    const getQuestionMandatoryConfig = React.useCallback((questionId: string, defaultValue: boolean = true): boolean => {
        if (!questionnaireConfig) {
            return false;
        }

        return questionnaireConfig && isQuestionMandatory(questionnaireConfig, questionId, defaultValue);
    }, [questionnaireConfig]);

    const updateAnswer = React.useCallback((payload) => {
        dispatch(updateQuestionAction({ ...payload, questionnaireKey: GENERIC_CASE_QUESTIONNAIRE_KEY }));
    }, [dispatch]);

    const removeQuestion = React.useCallback((payload) => {
        dispatch(removeQuestionAction({ ...payload, questionnaireKey: GENERIC_CASE_QUESTIONNAIRE_KEY }));
    }, [dispatch]);

    const returnToHomePage = React.useCallback(() => dispatch(push("/", { })), [dispatch]);

    const submitQuestionnaire = React.useCallback(async (answers: AnsweredQuestionWithId[]): Promise<boolean> => {
        if (!submitQuestionnaireAction)
        {
            return false;
        }

        await submitQuestionnaireAction(answers);
        if (answers.every(a => a.isValid)) {
            returnToHomePage();
            return false;
        }

        return true;
    }, [returnToHomePage, submitQuestionnaireAction]);

    // We don't have one to load but need something to pass to the ContextAwareProgressBarQuestionnaireContainer
    const loadQuestionnaire = React.useCallback(() => false, []);

    const changeHrRepresentative = React.useCallback((answer: QuestionAnswer) => {
        if (!questionnaireConfig) {
            return;
        }

        const newHrRepresentative = hrRepresentatives?.find(hrRepresentative => hrRepresentative.id === answer);
        const hrNameQuestionConfig = getQuestionConfig(questionnaireConfig.questions, questionIds.HrName) as CustomQuestionConfig;
        const hrEmailQuestionConfig = getQuestionConfig(questionnaireConfig.questions, questionIds.HrEmail) as CustomQuestionConfig;

        const isValid = !!newHrRepresentative;

        updateAnswer({
            sectionId: lineManagerHrSectionId, id: questionIds.HrName,
            question: {
                id: hrNameQuestionConfig.id,
                question: hrNameQuestionConfig.questionText,
                answer: newHrRepresentative?.name,
                isValid
            }
        });

        updateAnswer({
            sectionId: lineManagerHrSectionId, id: questionIds.HrEmail,
            question: {
                question: hrEmailQuestionConfig.questionText,
                answer: newHrRepresentative?.email,
                isValid
            }
        });
    }, [hrRepresentatives, questionnaireConfig, updateAnswer]);

    const changeOrgStructureLevel = React.useCallback((level: number, selectedId: string) => {
        setSelectedOrgStructureLevelIds(prevValue => [...prevValue.slice(0, level), selectedId]);

        const selectedName = orgLevels?.find(orgLevel => orgLevel.id === selectedId)?.name;
        const thisOrgLevelNameQuestion = questions.find(question => question.id === `${orgLevelQuestionId}${level + 1}Name`);

        if (thisOrgLevelNameQuestion) {
            updateAnswer({
                sectionId: furtherDetailsSectionId,
                id: thisOrgLevelNameQuestion.id,
                question: { ...thisOrgLevelNameQuestion, answer: selectedName }
            });
        }

        for (let i = level + 2; i <= 4; i++) {
            const orgLevelIdQuestion = questions.find(question => question.id === `${orgLevelQuestionId}${i}Id`);
            const orgLevelNameQuestion = questions.find(question => question.id === `${orgLevelQuestionId}${i}Name`);

            if (orgLevelIdQuestion) {
                updateAnswer({
                    sectionId: furtherDetailsSectionId,
                    id: orgLevelIdQuestion.id,
                    question: { ...orgLevelIdQuestion, answer: "" }
                });
            }

            if (orgLevelNameQuestion) {
                updateAnswer({
                    sectionId: furtherDetailsSectionId,
                    id: orgLevelNameQuestion.id,
                    question: { ...orgLevelNameQuestion, answer: "" }
                });
            }
        }

        if (loadOrganisationStructure) {
            loadOrganisationStructure(selectedId);
        }
    }, [loadOrganisationStructure, orgLevels, questions, updateAnswer]);

    return (
        <ContextAwareProgressBarQuestionnaireContainer
            loadQuestionnaire={loadQuestionnaire}
            submitQuestionnaire={submitQuestionnaire}
            readonly={readonly}
            questionnaireKey={GENERIC_CASE_QUESTIONNAIRE_KEY}
            questionnaireType={QuestionnaireTypes.Other}
        >
            <OtherServicesSection
                questionConfigs={questionnaireConfig.questions}
                genericCaseTypes={genericCaseTypes}
                readonly={readonly} />

            <DataPrivacySection questionConfigs={questionnaireConfig.questions} readonly={readonly} />

            <ManagerHrSection
                questionConfigs={questionnaireConfig.questions}
                currentUser={currentUser}
                hrRepresentatives={hrRepresentatives}
                readonly={readonly}
                getAnswerToQuestionWithId={getAnswerToQuestionWithId}
                changeHrRepresentative={changeHrRepresentative}
                getQuestionMandatoryConfig={getQuestionMandatoryConfig} />

            <EmployeeDetailsSection
                questionConfigs={questionnaireConfig.questions}
                readonly={readonly}
                getAnswerToQuestionWithId={getAnswerToQuestionWithId}
                getQuestionMandatoryConfig={getQuestionMandatoryConfig} />

            <EmployeeDetailsContinuedSection
                questionConfigs={questionnaireConfig.questions}
                orgLevels={orgLevels}
                selectedOrgStructureLevelIds={selectedOrgStructureLevelIds}
                readonly={readonly}
                changeOrgStructureLevel={changeOrgStructureLevel}
                getQuestionMandatoryConfig={getQuestionMandatoryConfig} />

            <SupportingDocumentSection
                questionConfigs={questionnaireConfig.questions}
                questions={questions}
                readonly={readonly}
                removeQuestion={removeQuestion}
                updateAnswer={updateAnswer} />

            {(moreSectionsForGenericForm || readonly) && <>
                <FurtherInformationSection
                    questionConfigs={questionnaireConfig.questions}
                    readonly={readonly} />

                {questionnaireConfig.additionalQuestions && <FurtherQuestionsSection
                    questionConfigs={questionnaireConfig.additionalQuestions}
                    readonly={readonly} />}
            </>}

            <ConfidentialitySection
                questionConfigs={questionnaireConfig.questions}
                readonly={readonly}
                getAnswerToQuestionWithId={getAnswerToQuestionWithId} />
        </ContextAwareProgressBarQuestionnaireContainer>
    );
};
